import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Navigation, Clock, Mail, ArrowRight } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import QuoteRequestModal from '../components/site/QuoteRequestModal';
import { useSeo } from '../lib/seo';

const LOCATIONS = [
  {
    id: 'orleans',
    brand: 'A+ AUTO GLASS',
    name: 'A+ Auto Glass (Orleans)',
    address_line1: '8-1439 Youville Dr',
    address_line2: 'Orleans, ON K1C 4M8',
    phone: '343.558.8820',
    tel: '3435588820',
    email: 'info@aplusautoglass.ca',
    hours: [
      { d: 'Monday – Friday', h: '8:00 AM – 5:00 PM' },
      { d: 'Saturday & Sunday', h: 'Closed' },
    ],
    mapQuery: '8-1439 Youville Dr, Orleans, ON K1C 4M8',
  },
  {
    id: 'gatineau',
    brand: "A+ VITRE D'AUTO",
    name: "A+ Vitre D'auto (Gatineau)",
    address_line1: 'By appointment',
    address_line2: 'Gatineau, QC',
    phone: '819.975.1750',
    tel: '8199751750',
    email: 'info@aplusautoglass.ca',
    hours: [
      { d: 'Monday – Friday', h: '8:00 AM – 5:00 PM' },
      { d: 'Saturday & Sunday', h: 'Closed' },
    ],
    mapQuery: 'Gatineau, QC, Canada',
  },
];

function useOnScreen(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >{children}</div>
  );
}

export default function Locations() {
  const [selected, setSelected] = useState(LOCATIONS[0]);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useSeo({
    title: 'Auto Glass Locations in Ottawa & Orleans | A+ Auto Glass',
    description:
      'Visit A+ Auto Glass in Orleans (8-1439 Youville Dr) or book mobile windshield service across Ottawa, Kanata, Gatineau, Nepean & Barrhaven. Call 343-558-8820.',
    path: '/locations',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const handler = () => setQuoteOpen(true);
    window.addEventListener('open-quote-modal', handler);
    return () => window.removeEventListener('open-quote-modal', handler);
  }, []);

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(selected.mapQuery)}&t=&z=14&ie=UTF8&iwloc=B&output=embed`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selected.mapQuery)}`;

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <Navbar />

      {/* HERO strip — matches Services / Why Choose Us / Contact */}
      <section className="relative overflow-hidden bg-[#0a1220] text-white">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#0D5BD7]/20 to-transparent pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-14">
          <h1 className="font-black leading-[1.05] tracking-[-0.015em] text-[32px] md:text-[42px] lg:text-[48px] max-w-[680px]">
            Find An A+ Auto Glass Near You
          </h1>
          <p className="mt-4 text-[15px] md:text-[16.5px] text-white/85 max-w-[600px] leading-[1.65]">
            Proudly serving Ottawa and the surrounding communities with professional windshield
            replacement, ADAS calibration and mobile service.
          </p>
        </div>
      </section>

      {/* Map + card */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Mobile quick switch */}
          <div className="lg:hidden flex gap-2 -mb-1" data-testid="location-switcher">
            {LOCATIONS.map((loc) => (
              <button
                key={loc.id}
                onClick={() => setSelected(loc)}
                data-testid={`location-chip-${loc.id}`}
                className={`flex-1 h-12 rounded-full border-2 text-[12px] font-bold tracking-[0.12em] transition-colors active:scale-[0.98] ${selected.id === loc.id ? 'border-[#0D5BD7] bg-[#0D5BD7] text-white' : 'border-slate-200 text-[#111827]'}`}
              >
                {loc.id === 'orleans' ? 'ORLEANS' : 'GATINEAU'}
              </button>
            ))}
          </div>
          {/* Map (desktop only) */}
          <Reveal className="hidden lg:block lg:col-span-3 order-1">
            <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-[0_2px_20px_rgba(15,23,42,0.06)] bg-white">
              <div className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-[440px] bg-slate-100">
                <iframe
                  key={selected.id}
                  title={`Map of ${selected.name}`}
                  src={mapSrc}
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </Reveal>

          {/* Location card list */}
          <Reveal className="lg:col-span-2 order-2" delay={100}>
            <div className="space-y-4">
              {LOCATIONS.map((loc) => {
                const isActive = loc.id === selected.id;
                return (
                  <div
                    key={loc.id}
                    onClick={() => setSelected(loc)}
                    className={`group cursor-pointer bg-white rounded-2xl border ${isActive ? 'border-[#0D5BD7]' : 'border-slate-100'} shadow-[0_2px_16px_rgba(15,23,42,0.05)] hover:shadow-[0_18px_40px_rgba(13,91,215,0.10)] hover:-translate-y-0.5 transition-all duration-300 overflow-hidden`}
                  >
                    <div className={`h-1 w-full ${isActive ? 'bg-[#0D5BD7]' : 'bg-transparent'}`} />
                    <div className="p-6 md:p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center shadow-md shrink-0">
                          <MapPin className="w-5 h-5" strokeWidth={1.8} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-bold tracking-[0.22em] text-[#0D5BD7]">{loc.brand || 'A+ AUTO GLASS'}</div>
                          <h3 className="mt-1 text-[20px] font-black text-[#111827] tracking-tight leading-tight">{loc.name}</h3>
                          <div className="mt-3 text-[14px] text-[#5F6773] leading-[1.7]">
                            <div>{loc.address_line1}</div>
                            <div>{loc.address_line2}</div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 space-y-2 text-[13.5px]">
                        <div className="flex items-center gap-2 text-[#111827]">
                          <Phone className="w-4 h-4 text-[#0D5BD7]" strokeWidth={1.8} />
                          <a href={`tel:${loc.tel}`} className="font-semibold hover:text-[#0D5BD7] transition-colors">{loc.phone}</a>
                        </div>
                        <div className="flex items-center gap-2 text-[#5F6773]">
                          <Mail className="w-4 h-4 text-[#0D5BD7]" strokeWidth={1.8} />
                          <a href={`mailto:${loc.email}`} className="hover:text-[#0D5BD7] transition-colors">{loc.email}</a>
                        </div>
                      </div>

                      <div className="mt-5 border-t border-slate-100 pt-5">
                        <div className="text-[11px] font-bold tracking-[0.2em] text-slate-500 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5" /> HOURS
                        </div>
                        <div className="mt-2 space-y-1 text-[13px]">
                          {loc.hours.map((h, i) => (
                            <div key={i} className="flex items-center justify-between text-[#5F6773]">
                              <span>{h.d}</span>
                              <span className={`font-semibold ${h.h === 'Closed' ? 'text-rose-600' : 'text-[#111827]'}`}>{h.h}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col sm:flex-row items-stretch gap-3">
                        <a
                          href={`tel:${loc.tel}`}
                          data-testid="call-location-btn"
                          className="group/btn inline-flex items-center justify-center w-full sm:w-auto sm:flex-1 h-14 sm:h-12 px-6 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[13px] sm:text-[12px] font-bold tracking-[0.18em] sm:tracking-[0.22em] transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px]"
                        >
                          <Phone className="w-4 h-4 mr-2" /> CALL LOCATION
                        </a>
                        <a
                          href={directionsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="get-directions-btn"
                          className="inline-flex items-center justify-center w-full sm:w-auto sm:flex-1 h-14 sm:h-12 px-6 rounded-full border-2 border-[#0D5BD7] text-[#0D5BD7] hover:bg-[#0D5BD7] hover:text-white text-[13px] sm:text-[12px] font-bold tracking-[0.18em] sm:tracking-[0.22em] transition-colors"
                        >
                          <Navigation className="w-4 h-4 mr-2" /> GET DIRECTIONS
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Serving area note */}
              <div className="bg-[#F8F9FB] border border-slate-100 rounded-2xl p-6">
                <div className="text-[12px] font-bold tracking-[0.24em] text-[#0D5BD7]">SERVICE AREA</div>
                <div className="mt-2 text-[14px] text-[#5F6773] leading-[1.7]">
                  We also offer mobile windshield service throughout Ottawa, Orleans, Kanata, Gatineau, Nepean, Barrhaven, and surrounding communities.
                </div>
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="mt-4 inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.2em] text-[#0D5BD7] hover:underline"
                >
                  BOOK MOBILE SERVICE <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />

      <QuoteRequestModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
